import { rem, darken } from 'polished';
import { css } from 'styled-components'

const colors = {
  white: '#fff',
  whiteDarker: '#F7F7F7',
  black: '#000',
  blackLight: '#333333',
  grey: '#808080',
  greyLighter: '#F5F5F5',
  greyLight: '#C8C8C8',
  greyLighS: '#EFEFEF',
  greyLightM: '#FAFAFA',
  greyDark: '#484848',
  green: '#92D200',
  blue: '#5295E9',
  blueLight: '#04B1BD',
  blueFocus: '#04B1BD',
  yellow: '#f7b609',
  orange: '#f05a24',
  red: '#EF7373',
  redLighter: '#E38A8A',
  redDarker: '#DF5858',
};

const colorsSemantic = {
  base: colors.black,
  baseInverse: colors.white,
  baseLighter: colors.greyDark,
  baseLighterS: colors.grey,
  primary: colors.green,
  primaryDarker: '#92D200',
  primaryLighter: '#CAE888',
  border: '#A2A2A2',
  borderLighter: '#EFEFEF',
  borderBtn: '#e3e3e3',
  bg: colors.white,
  bgInverse: colors.black,
  bgFocus: '#eaeaea',
  bgLighter: colors.white,
  bgLighterS: '#F8F7F8',
  bgDarker: colors.greyLight,
  bgDark: '#242424',
  bgPrimary: '#91D200',
  bgHover: 'rgba(0, 0, 0, 0.34)',
  bgAnimation: colors.greyLighS,
  danger: colors.red,
  dangerDarker: colors.redDarker,
  dangerLighter: colors.redLighter,
  warn: colors.yellow,
  warnDark: colors.yellow,
  warnLighter: colors.yellow,
  editorHighlight: colors.blueFocus,
  editorBg: colors.black,
};

const type = {
  base: "'Roboto', sans-serif",
  serif: "'Playfair Display', 'Roboto', sans-serif",
  icons: "'FontAwesome'",
};

const weight = {
  xxlight: 100,
  xlight: 200,
  light: 300,
  xbase: 400,
  base: 500,
  bold: 600,
};

const sizes = {
  xs3: rem('3px'),
  xs2: rem('8px'),
  xs: rem('12px'),
  s: rem('15px'),
  m: rem('16px'),
  l: rem('18px'),
  xl: rem('20px'),
  xl2: rem('22px'),
  xl3: rem('24px'),
  xl4: rem('32px'),
  xl5: rem('40px'),
  xl6: rem('48px'),
  xl7: rem('56px'),
  xl8: rem('64px'),
  xl9: rem('128px'),
};

const typos = {
  input: sizes.xs,
  button: sizes.xs,
  link: sizes.xs,
  text: sizes.l,
  title: sizes.xl6,
};

const spacing = {
  xs4: rem('4px'),
  xs3: rem('6px'),
  xs2: rem('8px'),
  xs: rem('12px'),
  s: rem('14px'),
  sm: rem('16px'),
  m: rem('20px'),
  l: rem('24px'),
  xl: rem('30px'),
  xl1: rem('32px'),
  xl2: rem('40px'),
  xl3: rem('50px'),
  xl4: rem('64px'),
  xl5: rem('100px'),
  xl6: rem('120px'),
};

const values = {
  borderSize: '1px',
  borderSizeM: '2px',
  borderRadius: '4px',
  borderRadiusM: '14px',
  zIndex: {
    s: '10',
    m: '50',
    l: '100',
    xl: '200',
    superBig: '9999',
  },
  headerLine: {
    width: '50px',
    height: '4px',
  },
};

const shadows = {
  button: '0 2px 3px 0 #EFEFEF',
  icon: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
  base: '0px 4px 16px 0px rgba(0, 0, 0, 0.1)',
  card: 'rgba(0, 0, 0, 0.06) 0px 3px 16px',
};

const mixins = {
  select: () => css`
  .react-select {
    &__dropdown-indicator {
      padding: 0;
    }
    &__single-value {
      max-width: 100%;
    }
    &__indicator-separator {
      padding: 0;
      margin-right: ${props => props.theme.spacing.xs3};
      ${props => !props.isMulti && 'display:none;'};
    }
    &__control {
      min-width: 120px;
      background-color: transparent;
      cursor: pointer;
      color: ${props => props.theme.colors.base};
      font-weight: ${props => props.theme.weight.xbase};
      width: 100%;
      border: 0;
      border-radius: 0;
      &--is-focused {
        border: 0;
        box-shadow: none;
      }
    }

    &__value-container {
      font-size: ${props => props.theme.sizes.s};
      padding: 0;
    }

    &__menu {
      border-radius: 0;
      box-shadow: ${props => props.theme.shadows.base};
      margin-top: 0;
      &-notice {
        font-size: ${props => props.theme.sizes.s};
        font-weight: ${props => props.theme.weight.xbase};
      }
    }
    &__option {
      font-size: ${props => props.theme.sizes.s};
      background-color: ${props => props.theme.colors.bg};
      color: ${props => props.theme.colors.base};
      &--is-selected {
        background-color: ${props => props.theme.colors.bgDark} !important;
        color: ${props => props.theme.colors.baseInverse} !important;
      }
      &--is-focused {
        background-color: ${props => darken(0.04, props.theme.colors.bgLighter)};
        color: ${props => props.theme.colors.base};
      }
      &--is-disabled {
        background-color: ${props => props.theme.colors.bg};
        color: ${props => props.theme.colors.grey};
        cursor: not-allowed;
      }
    }

    &__indicator {
      color: ${props => props.theme.colors.base};
    }

    &__multi-value {
      background-color: ${props => props.theme.colors.greyLighS};
      border-radius: ${props => props.theme.sizes.xs};
      &__label {
        font-size: ${props => props.theme.sizes.s};
      }
    }
  }
`
}

const theme = {
  colors: {
    ...colors,
    ...colorsSemantic,
  },
  type,
  sizes,
  typos,
  weight,
  values,
  spacing,
  shadows,
  mixins
};

export default theme;
